import { useCallback, useEffect, useRef, useState } from 'react';

import { apiGetRowLinkedFiles } from '../api/datasetFetch';
import type { RowLinkedFilesResult } from '../types/datasetTypes';

type CacheEntry = {
  result?: RowLinkedFilesResult;
  promise?: Promise<RowLinkedFilesResult>;
};

const rowLinkedFilesCache = new Map<string, CacheEntry>();

const buildCacheKey = (datasetId: string, rowId: string) => `${datasetId}:${rowId}`;

export const invalidateRowLinkedFilesCache = (datasetId?: string, rowId?: string) => {
  if (!datasetId) {
    rowLinkedFilesCache.clear();
    return;
  }

  if (rowId) {
    rowLinkedFilesCache.delete(buildCacheKey(datasetId, rowId));
    return;
  }

  for (const key of rowLinkedFilesCache.keys()) {
    if (key.startsWith(`${datasetId}:`)) {
      rowLinkedFilesCache.delete(key);
    }
  }
};

export function useRowLinkedFiles(
  datasetId: string,
  rowId: string,
  enabled: boolean,
  matchKey = '',
  tagsRevision = 0,
) {
  const [result, setResult] = useState<RowLinkedFilesResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const hasFetchedRef = useRef(false);
  const prevMatchKeyRef = useRef(matchKey);
  const matchKeyRef = useRef(matchKey);
  const resultRef = useRef<RowLinkedFilesResult | null>(null);

  matchKeyRef.current = matchKey;

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  const load = useCallback(async () => {
    const cacheKey = buildCacheKey(datasetId, rowId);
    const cached = rowLinkedFilesCache.get(cacheKey);
    if (cached?.result !== undefined && !cached.promise) {
      setResult(cached.result);
      setError(null);
      return cached.result;
    }

    if (cached?.promise) {
      if (!resultRef.current) {
        setIsLoading(true);
      }
      try {
        const resolved = await cached.promise;
        setResult(resolved);
        setError(null);
        return resolved;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load linked files';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!resultRef.current) {
      setIsLoading(true);
    }
    setError(null);

    const promise = apiGetRowLinkedFiles(datasetId, rowId);
    rowLinkedFilesCache.set(cacheKey, { promise });

    try {
      const resolved = await promise;
      rowLinkedFilesCache.set(cacheKey, { result: resolved });
      hasFetchedRef.current = true;
      if (requestIdRef.current === requestId) {
        setResult(resolved);
      }
      return resolved;
    } catch (err: unknown) {
      rowLinkedFilesCache.delete(cacheKey);
      const message = err instanceof Error ? err.message : 'Failed to load linked files';
      if (requestIdRef.current === requestId) {
        setError(message);
        setResult(null);
      }
      return null;
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [datasetId, rowId]);

  useEffect(() => {
    hasFetchedRef.current = false;
    prevMatchKeyRef.current = matchKeyRef.current;
    setResult(null);
  }, [datasetId, rowId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    rowLinkedFilesCache.delete(buildCacheKey(datasetId, rowId));

    const matchKeyChanged = prevMatchKeyRef.current !== matchKey;
    prevMatchKeyRef.current = matchKey;

    if (matchKeyChanged) {
      setResult(null);
    }

    if (hasFetchedRef.current) {
      void load();
    }
  }, [enabled, load, matchKey, tagsRevision, datasetId, rowId]);

  return {
    result,
    isLoading,
    error,
    reload: load,
  };
}
